import { EshopMethodPrice, EshopMoney } from '@/interfaces/eshop'

export type EshopPriceRow = {
  currency: string
  amount: number
  freeFrom: number | null
}

const centsToDecimal = (amount: string): number => parseInt(amount, 10) / 100

const decimalToCents = (amount: number): number => Math.round(amount * 100)

export const moneyAmount = (money: EshopMoney): number =>
  parseInt(money.amount, 10)

export const priceInclVat = (
  money: EshopMoney,
  taxRate?: number | null,
): EshopMoney => {
  if (taxRate === null || taxRate === undefined || taxRate <= 0) {
    return money
  }

  return {
    amount: String(Math.round(moneyAmount(money) * (100 + taxRate) / 100)),
    currency: money.currency,
  }
}

export const lineTotalInclVat = (
  unitPrice: EshopMoney,
  quantity: number,
  taxRate?: number | null,
): EshopMoney => {
  const unitInclVat = priceInclVat(unitPrice, taxRate)

  return {
    amount: String(moneyAmount(unitInclVat) * quantity),
    currency: unitInclVat.currency,
  }
}

// One row per configured account currency, prefilled from existing prices.
export const buildPriceRows = (
  currencies: string[],
  prices: EshopMethodPrice[],
): EshopPriceRow[] =>
  currencies.map((currency) => {
    const existing = prices.find((price) => price.price.currency === currency)

    return {
      currency,
      amount: existing ? centsToDecimal(existing.price.amount) : 0,
      freeFrom: existing?.freeFromThreshold
        ? centsToDecimal(existing.freeFromThreshold.amount)
        : null,
    }
  })

// PATCH shape: currency-keyed map with integer-cents amounts.
export const buildPricesPayload = (rows: EshopPriceRow[]) =>
  Object.fromEntries(
    rows.map((row) => [
      row.currency,
      {
        price: {
          amount: decimalToCents(row.amount),
          currency: row.currency,
        },
        freeFromThreshold:
          row.freeFrom !== null
            ? {
                amount: decimalToCents(row.freeFrom),
                currency: row.currency,
              }
            : null,
      },
    ]),
  )
